/**
 * WHAT A USER ROW IS ALLOWED TO CLAIM.
 *
 * Three separate questions, answered here rather than inside the page, so each
 * one can be asserted without Jest having to parse a screen it cannot load.
 *
 *   1. What does this account's status actually say?
 *   2. May an operator sign in as this person, and if not, why not?
 *   3. Did the estate come back empty, or did it not come back?
 *
 * ─── 1. THE STATUS THAT WAS A LIE ────────────────────────────────────────
 *
 * The page it replaces did this:
 *
 *     const { color, icon, label } = config[status] || config.active;
 *
 * `config` held three keys. Every other value the API can put in that field —
 * `suspended`, `locked`, `pending_deletion`, a new one added next quarter, a
 * null — fell through the `||` and was drawn as a GREEN TICK reading "Active".
 * Not "unknown", not the raw string: the specific, confident, wrong answer that
 * an operator uses to decide whether an account needs attention. A suspended
 * user and an active one were the same row.
 *
 * The rule here is the one the rest of this application already keeps: an
 * unrecognised value is reported AS ITSELF, in the muted tone, and the muted
 * tone means "no measurement", not "fine". `describeUserStatus` therefore has
 * no default branch that borrows another state's label.
 *
 * ─── WHY THE TONE KEYS ARE PREFIXED ──────────────────────────────────────
 *
 * `StatusBadge`'s TONE_MAP is shared across every concept in the portal and its
 * header says why unprefixed keys are a trap: `pending` is an invitation state
 * AND a subscription state AND a job state, and one concept silently inherits
 * another's colour. `user_*` keeps this family's meanings its own.
 *
 * INVITED IS `sky`, NOT AMBER, for the reason already written down against
 * `invite_pending`: an invitation sent and not yet accepted is the ordinary
 * condition of every new account, and drawing ordinary onboarding in the
 * caution tone teaches an operator to stop reading the caution tone.
 *
 * ─── 2. WHO MAY BE SIGNED IN AS ──────────────────────────────────────────
 *
 * The old button was `disabled={busy || user.status !== 'active'}` and said
 * nothing about why. Two different refusals — "this account cannot sign in at
 * all" and "this build does not recognise this account's state" — looked
 * identical to a greyed-out icon, and neither was explained. They are separated
 * here and each carries its sentence.
 *
 * ─── 3. UNAVAILABLE IS NOT NONE ──────────────────────────────────────────
 *
 * The company list caught its own load failure, emptied the array, and rendered
 * "No companies yet." An operator whose request had just failed was told the
 * platform has no customers. The two states are different facts and they now
 * have different names.
 */

/** Account states this build understands, with the words it uses for them. */
const STATUS = {
  active: { tone: 'user_active', label: 'Active', meaning: 'Can sign in.' },
  invited: { tone: 'user_invited', label: 'Invited', meaning: 'Invitation sent; not accepted yet.' },
  disabled: { tone: 'user_disabled', label: 'Disabled', meaning: 'Cannot sign in.' },
  suspended: { tone: 'user_suspended', label: 'Suspended', meaning: 'Cannot sign in.' },
  inactive: { tone: 'user_disabled', label: 'Inactive', meaning: 'Cannot sign in.' },
};

/** States in which the account itself can hold a session. */
const CAN_SIGN_IN = new Set(['active']);

export const USER_STATUS_KEYS = Object.keys(STATUS);

/**
 * A status, described truthfully.
 *
 * @param {string|null} status raw value from the API
 * @returns {{known: boolean, tone: string, label: string, meaning: string}}
 *   `tone` is a StatusBadge key; `known` states whether this build recognised
 *   the value, so a caller can render the difference rather than hide it.
 */
export const describeUserStatus = (status) => {
  const raw = String(status == null ? '' : status).trim();
  const key = raw.toLowerCase();
  const hit = STATUS[key];
  if (hit) return { known: true, tone: hit.tone, label: hit.label, meaning: hit.meaning };
  if (!raw) {
    return {
      known: false,
      tone: 'user_unknown',
      label: 'No status',
      meaning: 'This account has no status recorded.',
    };
  }
  return {
    known: false,
    tone: 'user_unknown',
    // ITS OWN VALUE, not a guess. An operator who sees a word this build does
    // not know can search for it; an operator shown "Active" cannot.
    label: raw.replace(/_/g, ' '),
    meaning: 'This portal does not recognise this status.',
  };
};

/** Account enablement does not prove that the person completed setup. */
export const describeUserSetup = (user) => {
  if (!user || String(user.status || '').toLowerCase() !== 'active' || user.last_login) return null;
  const onboarding = String(user.onboarding_state || '').toUpperCase();
  if (['NOT_SENT', 'SENT', 'SEND_FAILED', 'FAILED', 'EXPIRED', 'REVOKED'].includes(onboarding)) {
    return 'Setup pending';
  }
  return null;
};

export const LOGIN_AS = {
  AVAILABLE: 'available',
  /** Held by the operator's own permissions, not by anything about the user. */
  NOT_PERMITTED: 'not_permitted',
  /** The account cannot hold a session, so no session can be opened as it. */
  CANNOT_SIGN_IN: 'cannot_sign_in',
  /** The state is one this build cannot reason about. Refuse, and say so. */
  STATUS_UNKNOWN: 'status_unknown',
  /** A request for this user is already in flight. */
  BUSY: 'busy',
};

/**
 * Whether "Login as" is offered for a user, and the sentence shown when not.
 *
 * REFUSING ON AN UNRECOGNISED STATUS IS DELIBERATE. The alternative — treat
 * anything that is not a known blocked state as sign-in-able — is the same
 * shape of optimism that made every unknown status render as Active. Opening a
 * live customer session is not the place to guess.
 */
export const describeLoginAs = ({ status, permitted = true, busy = false } = {}) => {
  if (busy) {
    return { state: LOGIN_AS.BUSY, enabled: false, reason: 'Opening a session…' };
  }
  if (!permitted) {
    return {
      state: LOGIN_AS.NOT_PERMITTED,
      enabled: false,
      reason: 'Requires the tenant:impersonate permission.',
    };
  }
  const described = describeUserStatus(status);
  if (!described.known) {
    return {
      state: LOGIN_AS.STATUS_UNKNOWN,
      enabled: false,
      reason: `This account's status (${described.label}) is not one this portal recognises, so it will not open a session as them.`,
    };
  }
  const key = String(status).toLowerCase();
  if (!CAN_SIGN_IN.has(key)) {
    return {
      state: LOGIN_AS.CANNOT_SIGN_IN,
      enabled: false,
      reason: `${described.label} accounts cannot sign in, so there is no session to open.`,
    };
  }
  return { state: LOGIN_AS.AVAILABLE, enabled: true, reason: null };
};

export const ESTATE = {
  /** Loaded, and there is something to show. */
  LOADED: 'loaded',
  /** Loaded, and the answer really was nothing. */
  EMPTY: 'empty',
  /** Loaded nothing because a filter excludes everything — recoverable here. */
  FILTERED_OUT: 'filtered_out',
  /** Did not load. NOT the same fact as `EMPTY`, and never drawn as it. */
  UNAVAILABLE: 'unavailable',
};

/**
 * Which of the four a list is in.
 *
 * @param {{failed: boolean, rows: Array, filtered: boolean}} o
 */
export const describeList = ({ failed = false, rows = [], filtered = false } = {}) => {
  if (failed) return ESTATE.UNAVAILABLE;
  if (rows.length > 0) return ESTATE.LOADED;
  return filtered ? ESTATE.FILTERED_OUT : ESTATE.EMPTY;
};

/**
 * A person's initials for the flat row avatar.
 *
 * FLAT AND MONOCHROME, and that is the whole point of it living here rather
 * than reusing `TenantMark`. A coloured Cluvik tile means IDENTITY — a place in
 * the product — and the tenant mark's own notes record the fault: forty
 * dimensional tiles down a table say nothing, and they make the one mark on the
 * page that IS an area identity compete with forty imitations of itself. A user
 * is a row, not a destination. The company list above it still gets the tenant
 * mark, because a company is a place you go to.
 */
export const userInitials = (first, last, email) => {
  const a = String(first || '').trim();
  const b = String(last || '').trim();
  const letters = `${a[0] || ''}${b[0] || ''}`;
  if (letters) return letters.toUpperCase();
  const e = String(email || '').trim();
  return (e[0] || '?').toUpperCase();
};

export const userDisplayName = (user) => {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  return name || user?.email || 'this user';
};
